import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/system/Box";
import { CircularProgress, Typography, Divider, FormControlLabel } from "@mui/material";
import PrimaryButton from "../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryTextField from "../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryCheckbox from "../../../../Inputs/PrimaryCheckBox/PrimaryCheckBox";
import { handleAltEnter } from "../../../../../utils/keyboardShortcutHelpers";
import {
  findRateConfirmationContact,
  findRateConfirmationContactById,
  getRateConfirmationContactOptions
} from "../../../../../utils/rateConfirmationContactHelpers";
import { CarrierService } from "../../../../../services/CarrierService";
import { MovementService } from "../../../../../services/MovementService";
import { AlertContext, ActiveUserContext } from "../../../../Dashboard/Dashboard";

export default function RateCon({
  handleClose,
  activeLoad
}) {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [comments, setComments] = useState("");
  const [template, setTemplate] = useState(22016);
  const [templates, setTemplates] = useState([]);
  const [contactsSelected, setContactsSelected] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailChecked, setEmailChecked] = useState(true);
  const [carrier, setCarrier] = useState({});
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);

  const handleCancel = () => {
    handleClose();
  };

  const handleContactChange = value => {
    setContactsSelected(value);
    if (value.length > 0) {
      setSubmitDisabled(false);
    } else {
      setSubmitDisabled(true);
    }
  };

  const handlePreview = () => {
    const contact = findRateConfirmationContactById(contacts, contactsSelected[0])
      || findRateConfirmationContact(contacts, activeLoad);

    const payload = {
      movementId: activeLoad.movementId,
      templateNumber: template.toString(),
      print: true,
      contactEmail: contact?.email || activeLoad.carrierEmail || "",
      contactName: contact?.name || activeLoad.carrierContact || "Preview",
      contactId: contact?.id || ""
    };

    setIsLoading(true);
    MovementService.SendConfirmation(instance, payload).then(res => {
      res.arrayBuffer().then(data => {
        const file = new Blob([data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      });
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  async function handleSave() {
    setIsLoading(true);
    setSubmitDisabled(true);
    let submitAllSuccess = false;

    const payload = {
      orderId: activeLoad.id,
      movementId: activeLoad.movementId,
      templateNumber: template.toString(),
      print: false,
      fromEmail: activeUser.email,
      fromName: activeUser.userName,
      additionalComments: comments
    };

    for (let contactId of contactsSelected) {
      let contact = findRateConfirmationContactById(contacts, contactId);
      payload.contactEmail = contact.email;
      payload.contactName = contact.name;
      payload.contactId = contact.id;

      await MovementService.SendConfirmationEmail(instance, payload).then(() => {
        submitAllSuccess = true;
      }).catch(error => {
        console.error(error);
        submitAllSuccess = false;
        warning(error);
      });
    }

    if (submitAllSuccess) {
      alert("Confirmation sent successfully");
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (activeLoad.payeeId) {
      CarrierService.GetCarrier(instance, activeLoad.payeeId).then(res => {
        setCarrier(res);
      }).catch(error => {
        console.error(error);
        warning(error);
      });

      CarrierService.GetCarrierContacts(instance, activeLoad.payeeId).then(res => {
        const contactOptions = getRateConfirmationContactOptions(res);
        const selectedContact = findRateConfirmationContact(contactOptions, activeLoad);

        setContacts(contactOptions);
        setContactsSelected(selectedContact ? [selectedContact.id] : []);
        setSubmitDisabled(!selectedContact);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }
  }, [activeLoad]);

  useEffect(() => {
    setIsLoading(true);
    MovementService.GetConfirmationTemplates(instance).then(res => {
      setTemplates(res);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  }, []);

  return (
    <Box onClick={(e) => handleAltEnter(e, (submitDisabled || !emailChecked), handleSave)}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box>
          <Box>
            <Box sx={{ marginY: "20px" }}>
              <Typography variant="subhead2" color="primary.60">Add Ons</Typography>
              <Divider />
            </Box>
            <PrimaryTextField
              label="Additional Comments"
              onChange={setComments}
              value={comments}
              fullWidth
            />
            <Box sx={{ display: "flex", marginY: "10px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "end" }}>
                <Typography
                  sx={{
                    color: "primary.60",
                    cursor: "pointer"
                  }}
                  onClick={handlePreview}>
                  Preview
                </Typography>
                <PrimarySelect
                  label="Choose a template"
                  options={templates.map((o) => (o))}
                  onChange={setTemplate}
                  value={template}
                  flexGrow={1}
                  nameKey="descr"
                  valueKey="template_number"
                  disabled
                />
              </Box>
            </Box>
            <Box sx={{ marginY: "20px" }}>
              <Typography variant="subhead2" color="primary.60">Recipients</Typography>
              <Divider />
            </Box>
            <Box>
              <Typography variant="subhead2" marginY="10px">{carrier.name}</Typography>
              <Box display="flex" marginY="10px" maxWidth="500px" sx={{ alignItems: "center" }}>
                <PrimarySelect
                  label="Recipients"
                  options={contacts}
                  onChange={o => handleContactChange(o)}
                  value={contactsSelected}
                  flexGrow={1}
                  nameKey="recipientLabel"
                  valueKey="id"
                  disabled={isLoading || !emailChecked}
                  multiple
                />
                <FormControlLabel sx={{ marginLeft: "30px" }}
                  control={
                    <Box sx={{ marginRight: "8px" }}>
                      <PrimaryCheckbox
                        checked={emailChecked}
                        handleChange={e => setEmailChecked(e.target.checked)}
                        color="black_solid.60"
                        disabled={isLoading}
                      />
                    </Box>
                  }
                  label={
                    <Typography color="black_solid.80">Email</Typography>
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", marginTop: "9px", alignItems: "center" }}>
        <Box sx={{ margin: "0 20px 0 auto" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isLoading}
          />
        </Box>
        <Box sx={{ margin: "0 20px 0 0" }}>
          <SecondaryButton
            label="Preview Attachment"
            onClick={handlePreview}
            disabled={isLoading}
          />
        </Box>
        <PrimaryButton
          label="Send RateCon"
          onClick={handleSave}
          isLoading={isLoading}
          disabled={submitDisabled || !emailChecked}
        />
      </Box>
    </Box>
  );
}

RateCon.propTypes = {
  handleClose: PropTypes.func.isRequired,
  activeLoad: PropTypes.object.isRequired
};
